import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getPaymentStatus } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await getPaymentStatus(sessionId);
      setPaymentInfo(response.data);
      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else {
        // Poll a few times if not yet confirmed
        let attempts = 0;
        const pollInterval = setInterval(async () => {
          attempts++;
          try {
            const pollResponse = await getPaymentStatus(sessionId);
            if (pollResponse.data.payment_status === 'paid') {
              setPaymentInfo(pollResponse.data);
              setStatus('success');
              clearInterval(pollInterval);
            } else if (attempts >= 5) {
              setStatus('pending');
              clearInterval(pollInterval);
            }
          } catch {
            clearInterval(pollInterval);
            setStatus('error');
          }
        }, 2000);
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] flex items-center justify-center p-4">
      <Card className="bg-[#111827] border-white/10 max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-[#4A90D9] mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-bold text-white mb-2">Processing Payment</h1>
              <p className="text-slate-400">Please wait while we confirm your payment...</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
              <p className="text-slate-400 mb-4">
                Thank you for your payment of ${paymentInfo?.amount?.toFixed(2)} {paymentInfo?.currency?.toUpperCase()}.
              </p>
              <p className="text-sm text-slate-500 mb-6">
                A confirmation has been sent to your email.
              </p>
              <Button 
                onClick={() => navigate('/dashboard')}
                className="bg-[#4A90D9] hover:bg-[#3A80C9]"
                data-testid="back-to-dashboard"
              >
                Back to Dashboard
              </Button>
            </>
          )}
          
          {status === 'pending' && (
            <>
              <Loader2 className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2">Payment Processing</h1>
              <p className="text-slate-400 mb-6">
                Your payment is being processed. This may take a few moments.
              </p>
              <Button 
                onClick={() => navigate('/dashboard')}
                className="bg-[#4A90D9] hover:bg-[#3A80C9]"
              >
                Back to Dashboard
              </Button>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">❌</span>
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Payment Error</h1>
              <p className="text-slate-400 mb-6">
                We couldn&apos;t verify your payment. Please contact support if you were charged.
              </p>
              <Button 
                onClick={() => navigate('/dashboard')}
                className="bg-[#4A90D9] hover:bg-[#3A80C9]"
              >
                Back to Dashboard
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
